import { forwardRef } from 'react';
import { cn } from './cn';

// The single button in the app. Replaces the ~40 hand-rolled
// `py-[10px] px-[14px] rounded-[20px] …` strings that were copy-pasted across
// the views, plus SORT_PILL / NavBtn / ModeBtn.
//
// Every size keeps at least a 44px touch target, including compact controls.

const VARIANTS = {
  primary:
    'bg-primary-600 text-white border border-primary-600 hover:bg-primary-700 hover:border-primary-700 active:bg-primary-700',
  accent:
    'bg-accent-600 text-white border border-accent-600 hover:bg-accent-700 hover:border-accent-700 active:bg-accent-700',
  outline:
    'bg-surface text-ink-2 border border-line shadow-e1 hover:border-line-strong hover:text-ink active:bg-surface-2',
  ghost: 'bg-transparent text-ink-2 border border-transparent hover:bg-surface-3 hover:text-ink',
  danger:
    'bg-red-500 text-white border border-red-500 hover:bg-red-600 hover:border-red-600',
  quiet:
    'bg-surface-3 text-ink-2 border border-transparent hover:bg-surface-4 hover:text-ink',
};

const SIZES = {
  sm: 'min-h-11 min-w-11 px-3 text-[14.5px] gap-1.5',
  md: 'min-h-12 px-4 text-[16px] gap-2',
  lg: 'min-h-12 px-5 text-[17px] gap-2',
  icon: 'min-h-12 min-w-12 h-12 w-12 p-0',
  'icon-sm': 'min-h-11 min-w-11 h-11 w-11 p-0',
};

const BASE =
  'inline-flex shrink-0 items-center justify-center rounded-md font-display font-semibold tracking-[0.02em] ' +
  'whitespace-nowrap cursor-pointer select-none transition-[background-color,border-color,color,box-shadow] ' +
  'duration-150 disabled:cursor-not-allowed disabled:opacity-55 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500';

// forwardRef is required, not cosmetic: Radix primitives that render `asChild`
// (Menu trigger, Tooltip, Popover) clone this element and need its DOM node to
// anchor the floating content. Without a ref the popper never positions and the
// menu is left parked off-screen.
const Button = forwardRef(function Button(
  { variant = 'outline', size = 'md', block = false, className = '', type = 'button', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(BASE, VARIANTS[variant] ?? VARIANTS.outline, SIZES[size] ?? SIZES.md, block && 'w-full', className)}
      {...rest}
    >
      {children}
    </button>
  );
});

export default Button;
